/*! *********************************************************************
 *
 * Copyright 2019 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import React, { useState, useEffect } from 'react';
import { Labeled } from 'react-admin';
import PropTypes from 'prop-types';
import _find from 'lodash/find';
import _debounce from 'lodash/debounce';
import _differenceBy from 'lodash/differenceBy';

import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';

import dataProvider from '../providers/data-provider';

const AssetsSelect = ({ onChange, assetsInList }) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchAssets = async () => {
      const assets = await dataProvider.getList('assets', {
        filter: { q: search, status: ['published', 'draft'] },
      });

      // Filter existing assets
      if (assets.total !== 0) {
        const filteredAssets = _differenceBy(assets.data, assetsInList, 'id');

        setAssets(filteredAssets);
      }

      setLoading(false);
    };

    setLoading(true);
    fetchAssets();
  }, [search, assetsInList]);

  const debounceSearch = _debounce(setSearch, 300);

  const onInputChange = (e, value, reason) => {
    if (reason === 'reset' && value && value !== 'undefined') {
      const asset = _find(assets, (a) => optionLabel(a) === value);
      onChange(asset);
    } else if (reason === 'input') {
      debounceSearch(value);
    }
  };

  const optionLabel = (option) => {
    return `${option.extraData['ref']} - ${option.title}`;
  };

  return (
    <Labeled label="Choose asset from list" fullWidth>
      <Autocomplete
        loading={loading}
        options={assets}
        getOptionLabel={optionLabel}
        onInputChange={onInputChange}
        renderInput={(params) => <TextField {...params} variant="standard" />}
      />
    </Labeled>
  );
};

AssetsSelect.propTypes = {
  assets: PropTypes.array,
  onChange: PropTypes.func,
};

export default AssetsSelect;
